"use client"

import { useState, useEffect } from "react"
import {
    Card,
    Button,
    Input,
    Select,
    Tag,
    Modal,
    Image,
    Table,
    Space,
    Tooltip,
    message,
    Row,
    Col,
    Spin,
    Empty,
    Typography,
} from "antd"
import {
    AppstoreOutlined,
    UnorderedListOutlined,
    EyeOutlined,
    DownloadOutlined,
    DeleteOutlined,
    FileImageOutlined,
    FileTextOutlined,
    VideoCameraOutlined,
    FileOutlined,
    CalendarOutlined,
    FolderOutlined,
} from "@ant-design/icons"

const { Search } = Input
const { Option } = Select
const { Title, Text } = Typography

const ManageMedia = () => {
    const [mediaFiles, setMediaFiles] = useState([])
    const [filteredFiles, setFilteredFiles] = useState([])
    const [loading, setLoading] = useState(false)
    const [viewMode, setViewMode] = useState("grid") // 'grid' or 'list'
    const [selectedCategory, setSelectedCategory] = useState("all")
    const [searchTerm, setSearchTerm] = useState("")
    const [previewVisible, setPreviewVisible] = useState(false)
    const [previewFile, setPreviewFile] = useState(null)
    const [displayedCount, setDisplayedCount] = useState(20) // Initial number of files to show
    const [itemsPerPage] = useState(20) // Number of items to load per page

    // Extract file information from key
    const parseFileInfo = (file) => {
        const { key } = file // removed unused publicUrl
        const fileName = key.split("/").pop()
        const category = key.split("/")[0].replace(/"/g, "")
        const extension = fileName.split(".").pop().toLowerCase()

        // Determine file type
        let fileType = "other"
        let icon = <FileOutlined />

        if (["jpg", "jpeg", "png", "gif", "webp", "svg"].includes(extension)) {
            fileType = "image"
            icon = <FileImageOutlined style={{ color: "#52c41a" }} />
        } else if (["mp4", "avi", "mov", "wmv", "flv", "webm"].includes(extension)) {
            fileType = "video"
            icon = <VideoCameraOutlined style={{ color: "#1890ff" }} />
        } else if (["pdf", "doc", "docx", "txt", "rtf"].includes(extension)) {
            fileType = "document"
            icon = <FileTextOutlined style={{ color: "#fa8c16" }} />
        }

        // Extract date from filename if available
        const dateMatch = fileName.match(/(\d{8})/)
        const timeMatch = fileName.match(/(\d{6})/)
        let uploadDate = "Unknown"

        if (dateMatch && timeMatch) {
            const dateStr = dateMatch[1]
            const timeStr = timeMatch[1]
            const year = dateStr.substring(0, 4)
            const month = dateStr.substring(4, 6)
            const day = dateStr.substring(6, 8)
            const hour = timeStr.substring(0, 2)
            const minute = timeStr.substring(2, 4)
            const second = timeStr.substring(4, 6)
            uploadDate = `${year}-${month}-${day} ${hour}:${minute}:${second}`
        }

        return {
            ...file,
            fileName,
            category,
            fileType,
            extension,
            icon,
            uploadDate,
            size: "Unknown", // API doesn't provide size
        }
    }

    // Fetch media files from API
    const fetchMediaFiles = async () => {
        setLoading(true)
        try {
            const response = await fetch("https://worker.roboticsonwheels.in/api/ROBONIST_BUCKET")
            const data = await response.json()

            if (data.files) {
                const parsedFiles = data.files.map(parseFileInfo)
                setMediaFiles(parsedFiles)
                setFilteredFiles(parsedFiles)
            }
        } catch (error) {
            console.error("Error fetching media files:", error)
            message.error("Failed to load media files")
        } finally {
            setLoading(false)
        }
    }

    useEffect(() => {
        fetchMediaFiles()
    }, [])

    // Filter files based on category and search term
    useEffect(() => {
        let filtered = mediaFiles

        // Filter by category
        if (selectedCategory !== "all") {
            filtered = filtered.filter((file) => file.fileType === selectedCategory)
        }

        // Filter by search term
        if (searchTerm) {
            filtered = filtered.filter(
                (file) =>
                    file.fileName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                    file.category.toLowerCase().includes(searchTerm.toLowerCase()),
            )
        }

        setFilteredFiles(filtered)
        setDisplayedCount(itemsPerPage)
    }, [mediaFiles, selectedCategory, searchTerm, itemsPerPage])

    // Get category counts
    const getCategoryCounts = () => {
        const counts = {}
        mediaFiles.forEach((file) => {
            counts[file.fileType] = (counts[file.fileType] || 0) + 1
        })
        return counts
    }

    // Handle file preview
    const handlePreview = (file) => {
        setPreviewFile(file)
        setPreviewVisible(true)
    }

    // Handle file download
    const handleDownload = (file) => {
        const link = document.createElement("a")
        link.href = file.publicUrl
        link.download = file.fileName
        link.target = "_blank"
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        message.success("Download started")
    }

    // Handle file delete (placeholder - would need backend implementation)
    const handleDelete = async (file) => {
        Modal.confirm({
            title: "Delete File",
            content: `Are you sure you want to delete "${file.fileName}"?`,
            okText: "Delete",
            okType: "danger",
            onOk: async () => {
                try {
                    const response = await fetch(
                        `https://worker.roboticsonwheels.in/api/ROBONIST_BUCKET?key=${encodeURIComponent(file.key)}`,
                        {
                            method: "DELETE",
                            headers: {
                                "Content-Type": "application/json",
                            },
                        },
                    )

                    if (response.ok) {
                        message.success("File deleted successfully")
                        // Remove file from local state
                        const updatedFiles = mediaFiles.filter((f) => f.key !== file.key)
                        setMediaFiles(updatedFiles)
                        setFilteredFiles(
                            updatedFiles.filter((f) => {
                                const categoryMatch = selectedCategory === "all" || f.fileType === selectedCategory
                                const searchMatch =
                                    !searchTerm ||
                                    f.fileName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                                    f.category.toLowerCase().includes(searchTerm.toLowerCase())
                                return categoryMatch && searchMatch
                            }),
                        )
                    } else {
                        const errorData = await response.text()
                        message.error(`Failed to delete file: ${errorData}`)
                    }
                } catch (error) {
                    console.error("Delete error:", error)
                    message.error("Failed to delete file")
                }
            },
        })
    }

    const handleLoadMore = () => {
        setDisplayedCount((prev) => prev + itemsPerPage)
    }

    const getDisplayedFiles = () => {
        return filteredFiles.slice(0, displayedCount)
    }

    // Grid view component
    const GridView = () => {
        const displayedFiles = getDisplayedFiles()
        const hasMore = displayedCount < filteredFiles.length

        return (
            <div>
                <Row gutter={[16, 16]}>
                    {displayedFiles.map((file, index) => (
                        <Col xs={24} sm={12} md={8} lg={6} xl={4} key={file.key}>
                            <Card
                                hoverable
                                cover={
                                    file.fileType === "image" ? (
                                        <div
                                            style={{
                                                height: 200,
                                                overflow: "hidden",
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "center",
                                                backgroundColor: "#f5f5f5",
                                            }}
                                        >
                                            <Image
                                                src={file.publicUrl || "/placeholder.svg"}
                                                alt={file.fileName +  index}
                                                style={{ maxWidth: "100%", maxHeight: "100%", objectFit: "cover" }}
                                                preview={false}
                                            />
                                        </div>
                                    ) : (
                                        <div
                                            style={{
                                                height: 200,
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "center",
                                                backgroundColor: "#f5f5f5",
                                                fontSize: 48,
                                            }}
                                        >
                                            {file.icon}
                                        </div>
                                    )
                                }
                                actions={[
                                    <Tooltip key="preview" title="Preview">
                                        <Button type="text" icon={<EyeOutlined />} onClick={() => handlePreview(file)} />
                                    </Tooltip>,
                                    <Tooltip key="download" title="Download">
                                        <Button type="text" icon={<DownloadOutlined />} onClick={() => handleDownload(file)} />
                                    </Tooltip>,
                                    <Tooltip key="delete" title="Delete">
                                        <Button type="text" danger icon={<DeleteOutlined />} onClick={() => handleDelete(file)} />
                                    </Tooltip>,
                                ]}
                            >
                                <Card.Meta
                                    title={
                                        <Tooltip title={file.fileName}>
                                            <Text ellipsis style={{ width: "100%", display: "block" }}>
                                                {file.fileName}
                                            </Text>
                                        </Tooltip>
                                    }
                                    description={
                                        <Space direction="vertical" size="small" style={{ width: "100%" }}>
                                            <Tag color="blue" icon={<FolderOutlined />}>
                                                {file.category}
                                            </Tag>
                                            <Text type="secondary" style={{ fontSize: 12 }}>
                                                <CalendarOutlined /> {file.uploadDate}
                                            </Text>
                                        </Space>
                                    }
                                />
                            </Card>
                        </Col>
                    ))}
                </Row>

                {hasMore && (
                    <div style={{ textAlign: "center", marginTop: 24 }}>
                        <Button type="primary" size="large" onClick={handleLoadMore} style={{ minWidth: 120 }}>
                            Load More ({filteredFiles.length - displayedCount} remaining)
                        </Button>
                    </div>
                )}

                <div style={{ textAlign: "center", marginTop: 16, color: "#666" }}>
                    <Text type="secondary">
                        Showing {Math.min(displayedCount, filteredFiles.length)} of {filteredFiles.length} files
                    </Text>
                </div>
            </div>
        )
    }

    // Table columns for list view
    const columns = [
        {
            title: "File",
            dataIndex: "fileName",
            key: "fileName",
            render: (text, record) => (
                <Space>
                    {record.icon}
                    <Text>{text}</Text>
                </Space>
            ),
            sorter: (a, b) => a.fileName.localeCompare(b.fileName),
        },
        {
            title: "Category",
            dataIndex: "category",
            key: "category",
            render: (text) => (
                <Tag color="blue" icon={<FolderOutlined />}>
                    {text}
                </Tag>
            ),
            filters: [...new Set(mediaFiles.map((file) => file.category))].map((cat) => ({
                text: cat,
                value: cat,
            })),
            onFilter: (value, record) => record.category === value,
        },
        {
            title: "Type",
            dataIndex: "fileType",
            key: "fileType",
            render: (text) => (
                <Tag
                    color={text === "image" ? "green" : text === "video" ? "blue" : text === "document" ? "orange" : "default"}
                >
                    {text.charAt(0).toUpperCase() + text.slice(1)}
                </Tag>
            ),
            filters: [
                { text: "Image", value: "image" },
                { text: "Video", value: "video" },
                { text: "Document", value: "document" },
                { text: "Other", value: "other" },
            ],
            onFilter: (value, record) => record.fileType === value,
        },
        {
            title: "Upload Date",
            dataIndex: "uploadDate",
            key: "uploadDate",
            sorter: (a, b) => new Date(a.uploadDate) - new Date(b.uploadDate),
        },
        {
            title: "Actions",
            key: "actions",
            render: (_, record) => (
                <Space>
                    <Tooltip key="preview" title="Preview">
                        <Button type="text" icon={<EyeOutlined />} onClick={() => handlePreview(record)} />
                    </Tooltip>
                    <Tooltip key="download" title="Download">
                        <Button type="text" icon={<DownloadOutlined />} onClick={() => handleDownload(record)} />
                    </Tooltip>
                    <Tooltip key="delete" title="Delete">
                        <Button type="text" danger icon={<DeleteOutlined />} onClick={() => handleDelete(record)} />
                    </Tooltip>
                </Space>
            ),
        },
    ]

    const categoryCounts = getCategoryCounts()

    return (
        <div style={{ padding: 24 }}>
            <div style={{ marginBottom: 24 }}>
                <Title level={2}>Manage Media</Title>
                <Text type="secondary">Manage and organize your media files</Text>
            </div>

            {/* Category Stats */}
            <Row gutter={16} style={{ marginBottom: 24 }}>
                <Col span={6}>
                    <Card>
                        <div style={{ textAlign: "center" }}>
                            <FileImageOutlined style={{ fontSize: 24, color: "#52c41a", marginBottom: 8 }} />
                            <div style={{ fontSize: 20, fontWeight: "bold" }}>{categoryCounts.image || 0}</div>
                            <div style={{ color: "#666" }}>Images</div>
                        </div>
                    </Card>
                </Col>
                <Col span={6}>
                    <Card>
                        <div style={{ textAlign: "center" }}>
                            <VideoCameraOutlined style={{ fontSize: 24, color: "#1890ff", marginBottom: 8 }} />
                            <div style={{ fontSize: 20, fontWeight: "bold" }}>{categoryCounts.video || 0}</div>
                            <div style={{ color: "#666" }}>Videos</div>
                        </div>
                    </Card>
                </Col>
                <Col span={6}>
                    <Card>
                        <div style={{ textAlign: "center" }}>
                            <FileTextOutlined style={{ fontSize: 24, color: "#fa8c16", marginBottom: 8 }} />
                            <div style={{ fontSize: 20, fontWeight: "bold" }}>{categoryCounts.document || 0}</div>
                            <div style={{ color: "#666" }}>Documents</div>
                        </div>
                    </Card>
                </Col>
                <Col span={6}>
                    <Card>
                        <div style={{ textAlign: "center" }}>
                            <FileOutlined style={{ fontSize: 24, color: "#666", marginBottom: 8 }} />
                            <div style={{ fontSize: 20, fontWeight: "bold" }}>{categoryCounts.other || 0}</div>
                            <div style={{ color: "#666" }}>Others</div>
                        </div>
                    </Card>
                </Col>
            </Row>

            {/* Filters and Controls */}
            <Card style={{ marginBottom: 24 }}>
                <Row gutter={16} align="middle">
                    <Col flex="auto">
                        <Search
                            placeholder="Search files..."
                            allowClear
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            style={{ maxWidth: 300 }}
                        />
                    </Col>
                    <Col>
                        <Select value={selectedCategory} onChange={setSelectedCategory} style={{ width: 150 }}>
                            <Option value="all">All Types</Option>
                            <Option value="image">
                                <Space>
                                    <FileImageOutlined />
                                    Images
                                </Space>
                            </Option>
                            <Option value="video">
                                <Space>
                                    <VideoCameraOutlined />
                                    Videos
                                </Space>
                            </Option>
                            <Option value="document">
                                <Space>
                                    <FileTextOutlined />
                                    Documents
                                </Space>
                            </Option>
                            <Option value="other">
                                <Space>
                                    <FileOutlined />
                                    Others
                                </Space>
                            </Option>
                        </Select>
                    </Col>
                    <Col>
                        <Button.Group>
                            <Button
                                type={viewMode === "grid" ? "primary" : "default"}
                                icon={<AppstoreOutlined />}
                                onClick={() => setViewMode("grid")}
                            >
                                Grid
                            </Button>
                            <Button
                                type={viewMode === "list" ? "primary" : "default"}
                                icon={<UnorderedListOutlined />}
                                onClick={() => setViewMode("list")}
                            >
                                List
                            </Button>
                        </Button.Group>
                    </Col>
                    <Col>
                        <Button onClick={fetchMediaFiles} loading={loading}>
                            Refresh
                        </Button>
                    </Col>
                </Row>
            </Card>

            {/* Content */}
            <Spin spinning={loading}>
                {filteredFiles.length === 0 ? (
                    <Empty description="No media files found" />
                ) : viewMode === "grid" ? (
                    <GridView />
                ) : (
                    <Table
                        columns={columns}
                        dataSource={filteredFiles}
                        rowKey={(record) => record.key} // removed unused index
                        pagination={{
                            pageSize: 20,
                            showSizeChanger: true,
                            showQuickJumper: true,
                            showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} files`,
                        }}
                    />
                )}
            </Spin>

            {/* Preview Modal */}
            <Modal
                title={previewFile?.fileName}
                open={previewVisible}
                onCancel={() => setPreviewVisible(false)}
                footer={[
                    <Button key="download" icon={<DownloadOutlined />} onClick={() => handleDownload(previewFile)}>
                        Download
                    </Button>,
                    <Button key="close" onClick={() => setPreviewVisible(false)}>
                        Close
                    </Button>,
                ]}
                width={800}
            >
                {previewFile && (
                    <div style={{ textAlign: "center" }}>
                        {previewFile.fileType === "image" ? (
                            <Image
                                src={previewFile.publicUrl || "/placeholder.svg"}
                                alt={previewFile.fileName}
                                style={{ maxWidth: "100%", maxHeight: 500 }}
                            />
                        ) : previewFile.fileType === "video" ? (
                            <video controls style={{ maxWidth: "100%", maxHeight: 500 }} src={previewFile.publicUrl}>
                                Your browser does not support the video tag.
                            </video>
                        ) : (
                            <div style={{ padding: 40, textAlign: "center" }}>
                                <div style={{ fontSize: 64, marginBottom: 16 }}>{previewFile.icon}</div>
                                <Title level={4}>{previewFile.fileName}</Title>
                                <Space direction="vertical">
                                    <Tag color="blue">{previewFile.category}</Tag>
                                    <Text type="secondary">Upload Date: {previewFile.uploadDate}</Text>
                                    <Text type="secondary">Type: {previewFile.fileType}</Text>
                                </Space>
                            </div>
                        )}
                    </div>
                )}
            </Modal>
        </div>
    )
}

export default ManageMedia
